// Bridges the shared useLiveTracking follow-lock state to the Leaflet map:
//   • auto-pans the camera to `userPos` whenever tracking is engaged AND
//     `isFollowing` is true (skipped on the very first fix — the LocateBtn
//     handles that flyTo directly)
//   • unlocks the follow state on ANY manual map interaction — dragstart,
//     touchstart, wheel, dblclick — so a walker's touchscreen inputs never
//     fight the auto-pan. Once unlocked, the user roams freely until they
//     tap the Locate Me button again to re-engage.
//
// This lives as a React-Leaflet child so it can `useMap()` + `useMapEvents()`.
import { useEffect, useRef } from "react";
import { useMap, useMapEvents } from "react-leaflet";

export default function FollowController({ userPos, isFollowing, setIsFollowing }) {
  const map = useMap();
  const firstFixRef = useRef(true);
  const programmaticMoveRef = useRef(false); // ignore our own flyTo-induced movestart

  // Manual-interaction listeners → break the follow lock. dragstart +
  // touchstart cover both desktop drag and mobile finger drag; wheel /
  // dblclick catch zoom-in from the corners; movestart is the catch-all
  // for stray keyboard pans but is guarded against our own flyTo() by
  // programmaticMoveRef.
  useMapEvents({
    dragstart: () => setIsFollowing(false),
    touchstart: () => setIsFollowing(false),
    wheel: () => setIsFollowing(false),
    dblclick: () => setIsFollowing(false),
    movestart: () => {
      if (programmaticMoveRef.current) {
        programmaticMoveRef.current = false;
        return;
      }
      // Untouched — dragstart above already handled genuine user drags.
      // This branch catches nothing else on desktop but is here so a
      // future manual pan mechanism doesn't sneak past the guard.
    },
  });

  useEffect(() => {
    if (!userPos) return;
    if (firstFixRef.current) {
      // Skip: LocateBtn already flew to the first fix.
      firstFixRef.current = false;
      return;
    }
    if (!isFollowing) return; // free-roam — leave the camera alone
    programmaticMoveRef.current = true;
    map.flyTo(userPos.position, Math.max(map.getZoom(), 15), {
      duration: 0.6,
      easeLinearity: 0.25,
    });
  }, [userPos, isFollowing, map]);

  return null;
}
